import { IContractTab, infoState } from "../../ContractService";
import { Contract } from "../../model/contract";
import { vsComponentName as contractAmendmentsComponentName } from "../../components/amendments/component";
import { IPromise } from "angular";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../records/server/serverUtility";

import { ServiceResponse } from "../../../../records/server/query";
import {
  vsComponentName as RecordFormViewComponentName,
  FormViewService,
  vsServiceName as FormViewServiceName,
  FormViewConfig,
} from "../../../../records/forms/formView";

import { StateParams, StateService } from "@uirouter/angularjs";
import { Record } from "../../../../records/server/crud";

import {
  ChangeJournalModalService,
  vsServiceName as ChangeJournalModalServiceName,
} from "../../../../records/changeJournal/changeNoteModalService";

/**
 * Configuration interface for creating a reusable Amendment tab.
 */
export interface ContractAmendmentTabConfig {
  amendmentObjectName: string;
  apiBaseUrl: string;
  parentIdProperty: string;
  title: string;
  detailComponent?: string;
}

/**
 * Returns a reusable Amendments tab configuration for contracts.
 */
export function getContractAmendmentTab(
  config: ContractAmendmentTabConfig,
): IContractTab {
  const amendmentBaseState = `${infoState}.amendments`;

  return <IContractTab>{
    name: "Amendments",
    uiSref: "amendments",
    state: {
      name: amendmentBaseState,
      url: "/amendments?amendmentId",
      params: { amendmentId: undefined },
      views: {
        tabs: {
          component: contractAmendmentsComponentName,
        },
      },
      resolve: {
        apiUrl: [() => config.apiBaseUrl],
        title: [() => config.title],
        detailComponent: [() => config.detailComponent],
        formViewConfig: [
          FormViewServiceName,
          (formViewService: FormViewService) =>
            formViewService.getFormViewConfig(
              config.amendmentObjectName,
            ),
        ],
        amendments: [
          "contract",
          ServerUtilityName,
          (contract: Contract, serverUtility: ServerUtility) =>
            getContractAmendments(
              serverUtility,
              contract,
              config.apiBaseUrl,
            ),
        ],
        amendmentId: [
          "$stateParams",
          ($stateParams: StateParams) => {
            const num = Number.parseInt(
              $stateParams["amendmentId"],
            );
            return Number.isNaN(num) ? undefined : num;
          },
        ],
        returnState: () => `${infoState}.amendment`,
      },
    },
    children: [
      {
        name: "add",
        uiSref: "add",
        state: {
          name: `${amendmentBaseState}.add`,
          url: "/add",
          views: {
            "tabs@main.contract.type.info": {
              component: RecordFormViewComponentName,
            },
          },
          resolve: {
            fields: [
              "formViewConfig",
              (config: FormViewConfig) => config.fields,
            ],
            model: [
              "contract",
              (contract: Contract) => ({
                [config.parentIdProperty]: contract.Id,
              }),
            ],
            title: [() => "Add Amendment"],
            onSubmit: [
              "$state",
              ServerUtilityName,
              ($state: StateService, serverUtility: ServerUtility) => [
                "model",
                (model: any) =>
                  serverUtility
                    .sendApiRequest({
                      url: `${config.apiBaseUrl}/${model[config.parentIdProperty]}/amendments`,
                      method: "post",
                      data: model,
                    })
                    .then(() => {
                      $state.go("^", undefined, { reload: true });
                    }),
              ],
            ],
            onCancel: [
              "$state",
              ($state: StateService) => () => $state.go("^"),
            ],
            showButtonsAtTop: [() => false],
          },
        },
      },
      {
        name: "edit",
        uiSref: "edit",
        state: {
          name: `${amendmentBaseState}.edit`,
          url: "/:amendmentId/edit",
          views: {
            "tabs@main.contract.type.info": {
              component: RecordFormViewComponentName,
            },
          },
          resolve: {
            fields: [
              "formViewConfig",
              (config: FormViewConfig) => config.fields,
            ],
            model: [
              "amendments",
              "$stateParams",
              (amendments: Record[], $stateParams: StateParams) =>
                amendments.find(
                  (a) => a.Id === +$stateParams["amendmentId"],
                ),
            ],
            title: [() => "Edit Amendment"],
            onSubmit: [
              "$state",
              ServerUtilityName,
              ChangeJournalModalServiceName,
              (
                $state: StateService,
                serverUtility: ServerUtility,
                changeJournalModalService: ChangeJournalModalService,
              ) =>
                (model: any) =>
                  changeJournalModalService
                    .getAndSetChangeJournalNote(model)
                    .then(() =>
                      serverUtility.sendApiRequest({
                        url: `${config.apiBaseUrl}/amendments/${model.Id}`,
                        method: "put",
                        data: model,
                      }),
                    )
                    .then(() => {
                      $state.go("^", undefined, { reload: true });
                    }),
            ],
            onCancel: [
              "$state",
              ($state: StateService) => () => $state.go("^"),
            ],
            showButtonsAtTop: [() => false],
          },
        },
      },
    ],
  };
}

/**
 * Fetches amendments for a given contract.
 */
function getContractAmendments(
  serverUtility: ServerUtility,
  contract: Contract,
  apiBaseUrl: string,
): IPromise<any[]> {
  return serverUtility
    .sendApiRequest<ServiceResponse<any[]>>({
      url: `${apiBaseUrl}/${contract.Id}/amendments`,
      method: "get",
    })
    .then((response) => response.Result);
}